import { types, getParent, type IAnyModelType } from 'mobx-state-tree';
import ObjectType, { type IObjectType } from './object_type';
import { modelReference } from '../refHelper';
import type { IMetaModel } from './index';
import type { IMethod } from './method';
import type { IMultiplePropagationConstraint } from './multiple_propagation_constraint';

/**
 * The Dependency instance interface.
 *
 * HAND-DECLARED so the loose `modelReference` targets (`master`/`dependent`) read
 * as concrete `IObjectType`, and so sibling views see each other.
 */
export interface IDependency {
  readonly id: string;
  readonly master: IObjectType;
  readonly dependent: IObjectType;
  readonly name: { master: string; dependent: string };
  readonly type: 'OPTIONAL_N' | 'OPTIONAL_1' | 'MANDATORY_N' | 'MANDATORY_1';
  asString(): string;
  readonly displayName: string;
  readonly displayString: string;
  readonly metamodel: IMetaModel;
  findAcquiredMethods(): IMethod[];
  findDownstreamOwnedMethods(start?: IDependency | null): IMethod[];
  readonly dependentMultiplePropagationConstraints: IMultiplePropagationConstraint[];
  readonly previousObject: IObjectType;
  readonly nextObject: IObjectType;
  setDependent(dependent: IObjectType): void;
  setMaster(master: IObjectType): void;
  setName(name: { master?: string; dependent?: string }): void;
  setType(type: 'OPTIONAL_N' | 'OPTIONAL_1' | 'MANDATORY_N' | 'MANDATORY_1'): void;
}

/**
 * A MERODE dependency.
 *
 * For the references to other MST nodes, we don't use types.reference directly,
 * but our custom wrapper. See '../refHelper.js' for the reason why.
 */
const Dependency = types
  .model('Dependency', {
    id: types.identifier,
    master: modelReference(types.late((): IAnyModelType => ObjectType)),
    dependent: modelReference(types.late((): IAnyModelType => ObjectType)),
    // This is different from JMermaid. A dependency now has a name on the
    // master-side and a name on the dependent-side. The import functionality
    // will put the single name in the MXP-file in both. The export
    // functionality will convert it to master_dependent format when going back
    // to MXP.
    name: types.model({
      master: types.string,
      dependent: types.string,
    }),
    type: types.enumeration(['OPTIONAL_N', 'OPTIONAL_1', 'MANDATORY_N', 'MANDATORY_1']),
  })
  .views((self0) => {
    const self = self0 as unknown as IDependency;
    return {
    // Human-readable label, e.g. for `${dependency}` interpolation in messages.
    // (MST 7 forbids declaring a view named `toString`, so this is a plain view.)
    asString() {
      return self.displayName;
    },
    // How to show the name in the UI
    get displayName() {
      return `${self.name.master} / ${self.name.dependent}`;
    },
    // Used in the logger and the report view
    get displayString() {
      return `Dependency ${self.displayName}`;
    },
    // convenience method to get the metamodel
    get metamodel(): IMetaModel {
      // up 2-levels because 1 level up is the array of dependencies.
      return getParent(self0, 2);
    },
    // Find acquired methods that have this dependency as their viaDependency
    findAcquiredMethods() {
      return self.metamodel.acquiredMethods.filter((m) => m.viaDependency === self);
    },
    // Built a list of all downstream owned methods. Used to add a
    // OwnedMethodEventOwningCheck for each of them when adding a new
    // dependency.
    findDownstreamOwnedMethods(start: IDependency | null = null): IMethod[] {
      // Protect against loops. This cannot happen in a consistent model, but
      // this method gets called during consistency checking!
      if (start === self) {
        return [];
      } else {
        let methods = self.dependent.ownedMethods.slice();
        self.dependent.dependenciesAsMaster.forEach((d) => {
          methods = methods.concat(d.findDownstreamOwnedMethods(start || self));
        });
        return methods;
      }
    },
    // get the MPCs that depend on this dependency
    get dependentMultiplePropagationConstraints() {
      return self.metamodel.multiplePropagationConstraints.filter((mpc) => mpc.dependsOnEdge(self));
    },
    // Get the previous object when this dependency appears in a path
    get previousObject() {
      return self.master;
    },
    // Get the next object when this dependency appears in a path
    get nextObject() {
      return self.dependent;
    },
    };
  })
  .actions((self) => ({
    setDependent(dependent: IObjectType) {
      self.dependent = dependent;
    },
    setMaster(master: IObjectType) {
      self.master = master;
    },
    setName({ master, dependent }: { master?: string; dependent?: string }) {
      self.name.master = master || self.name.master;
      self.name.dependent = dependent || self.name.dependent;
    },
    setType(type: 'OPTIONAL_N' | 'OPTIONAL_1' | 'MANDATORY_N' | 'MANDATORY_1') {
      self.type = type;
    },
  }));

export default Dependency;
